import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Check } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';

const PropertyGallery = ({ title, description, features, images, theme }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const themeColors = {
    hotel: { primary: '#0A2540', secondary: '#2C5F66', accent: '#C9A961' },
    villa: { primary: '#1E3A5F', secondary: '#4A7C85', accent: '#C9A961' },
    restaurant: { primary: '#2C5F66', secondary: '#0A2540', accent: '#D4AF37' }
  };

  const colors = themeColors[theme] || themeColors.hotel;

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <h3 className="text-3xl font-bold text-[#0A2540] mb-4">{title}</h3>
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">{description}</p>
          <div className="grid grid-cols-1 gap-3">
            {features.slice(0, 6).map((feature, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: colors.accent }}>
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl cursor-pointer" onClick={() => openLightbox(0)}>
            <img 
              src={images[0]?.url} 
              alt={images[0]?.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
              <div>
                <h4 className="text-white font-bold text-xl">{images[0]?.title}</h4>
                <p className="text-slate-200 text-sm">{images[0]?.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {images.slice(1).map((image, idx) => (
          <Card 
            key={idx}
            className="overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-slate-200"
            onClick={() => openLightbox(idx + 1)}
          >
            <div className="aspect-square relative">
              <img 
                src={image.url} 
                alt={image.title}
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <div>
                  <p className="text-white font-semibold text-sm">{image.title}</p>
                  <p className="text-slate-300 text-xs">{image.description}</p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-6xl p-0 bg-black">
          <div className="relative">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-20 w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            <div className="relative aspect-video">
              <img 
                src={images[currentImageIndex]?.url} 
                alt={images[currentImageIndex]?.title}
                className="w-full h-full object-contain"
              />
              
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </div>
            
            <div className="bg-black/90 backdrop-blur-md p-6 text-white">
              <h3 className="text-xl font-bold mb-2">{images[currentImageIndex]?.title}</h3>
              <p className="text-slate-300">{images[currentImageIndex]?.description}</p>
              <p className="text-sm text-slate-400 mt-2">{currentImageIndex + 1} / {images.length}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyGallery;